import { type CrossBrandMallHomeCampaign } from '@hooks-api/types/dataModel/cross-brand-mall'
import { Box } from '@pets-ui/Box/Box'
import { HStack } from '@pets-ui/HStack/HStack'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import FireIcon from '../../assets/fire-icon.svg?react'
import { useEligibleBrandSelection } from '../hooks/use-eligible-brand-selection'
import { useHomeAuthorizedCampaigns } from '../hooks/use-home-authorized-campaigns'
import { BrandHotProductSectionContent, BrandHotProductSectionContentSkeleton } from './BrandHotProductSectionContent'
import { BrandTabList } from './BrandTabList'
import { BrandTopSellerAvailabilityChecker } from './BrandTopSellerAvailabilityChecker'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  contentRow: {
    flexDirection: { [MOBILE]: 'column', default: 'row' },
    flexWrap: 'nowrap',
  },
  tabSidebar: {
    borderColor: colors.grey200,
    borderStyle: 'solid',
    flexShrink: 0,
    borderBottomWidth: { [MOBILE]: 1, default: 0 },
    borderRightWidth: { [MOBILE]: 0, default: 1 },
    width: { [MOBILE]: '100%', default: 120 },
  },
})

interface BrandHotProductSectionProps {
  campaigns: CrossBrandMallHomeCampaign[]
  currency: string
  isDetailLoading: boolean
  basePath: string
}

interface BrandHotProductSectionInnerProps {
  campaigns: CrossBrandMallHomeCampaign[]
  currency: string
  basePath: string
}

const BrandHotProductSectionHeader = () => (
  <HStack
    p={4}
    alignItems="center"
    backgroundColor={colors.white}
    borderBottomWidth={1}
    borderBottomStyle="solid"
    borderBottomColor={colors.grey200}
  >
    <HStack gap={2} alignItems="center">
      <FireIcon />
      <Text variant="h5" bold color={colors.grey900}>熱銷商品</Text>
      <Box borderColor={colors.red600} borderWidth={1} borderTopLeftRadius={8} borderTopRightRadius={8} borderBottomRightRadius={8} px={2}>
        <Text color={colors.red600}>熟客回流秘密武器</Text>
      </Box>
    </HStack>
  </HStack>
)

const BrandHotProductSectionSkeleton = () => (
  <HStack rootStyle={styles.contentRow}>
    <Box rootStyle={styles.tabSidebar}>
      <BrandTabList
        campaigns={[]}
        activeCampaignId={null}
        onChange={() => undefined}
        isLoading={true}
      />
    </Box>
    <BrandHotProductSectionContentSkeleton />
  </HStack>
)

const BrandHotProductSectionInner = (props: BrandHotProductSectionInnerProps) => {
  const { campaigns, currency, basePath } = props

  const authorizedCampaigns = useHomeAuthorizedCampaigns(campaigns)

  const {
    orderedCampaigns,
    eligibleCampaigns,
    selectedCampaign: activeCampaign,
    isAvailabilityResolved,
    setAvailability,
    setSelectedCampaignId,
  } = useEligibleBrandSelection(authorizedCampaigns)

  if (orderedCampaigns.length === 0) return null

  if (isAvailabilityResolved && eligibleCampaigns.length === 0) return null

  return (
    <Paper borderRadius={8} elevation={0} overflow="hidden">
      <VStack>
        <BrandHotProductSectionHeader />
        {orderedCampaigns.map(campaign => (
          <BrandTopSellerAvailabilityChecker key={campaign.campaign_id} campaign={campaign} onAvailabilityChange={setAvailability} />
        ))}
        {!isAvailabilityResolved
          ? <BrandHotProductSectionSkeleton />
          : (
            <HStack rootStyle={styles.contentRow}>
              <Box rootStyle={styles.tabSidebar}>
                <BrandTabList
                  campaigns={eligibleCampaigns}
                  activeCampaignId={activeCampaign?.campaign_id ?? null}
                  onChange={setSelectedCampaignId}
                  isLoading={false}
                />
              </Box>
              {activeCampaign && (
                <BrandHotProductSectionContent
                  key={activeCampaign.campaign_id}
                  campaign={activeCampaign}
                  currency={currency}
                  basePath={basePath}
                />
              )}
            </HStack>
          )}
      </VStack>
    </Paper>
  )
}

export const BrandHotProductSection = (props: BrandHotProductSectionProps) => {
  const { campaigns, currency, isDetailLoading, basePath } = props

  if (isDetailLoading) {
    return (
      <Paper borderRadius={8} elevation={0} overflow="hidden">
        <VStack>
          <BrandHotProductSectionHeader />
          <BrandHotProductSectionSkeleton />
        </VStack>
      </Paper>
    )
  }

  return (
    <BrandHotProductSectionInner
      campaigns={campaigns}
      currency={currency}
      basePath={basePath}
    />
  )
}

